import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { signUp, signInWithGoogle, resendVerification, signOut } from "../lib/dataStore";
import { toast } from "sonner";
import { MailCheck, Info } from "lucide-react";

export default function Signup() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get("redirect") || "/";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // set once we need verification
  const [errorNotice, setErrorNotice] = useState(null);

  const handleGoogleSignUp = async () => {
    setBusy(true);
    setErrorNotice(null);
    try {
      await signInWithGoogle();
      toast.success("Account connected with Google. Welcome!");
      nav(redirectPath);
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user" && err.code !== "auth/cancelled-popup-request") {
        toast.error(err.message || "Google sign up failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    setErrorNotice(null);
    try {
      const user = await signUp(form);
      if (user._needsVerification) {
        setPending(user.email);
        toast.success("Verification email sent");
      } else {
        toast.success("Account created. Welcome!");
        nav(redirectPath);
      }
    } catch (err) {
      const msg = err.message || "Sign up failed";
      if (msg.includes("Firebase Console") || msg.includes("operation-not-allowed") || msg.includes("disabled")) {
        setErrorNotice(msg);
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (pending) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-8">
        <div className="max-w-md text-center border border-border p-10 bg-card">
          <MailCheck className="w-12 h-12 text-terracotta mx-auto mb-4" />
          <h1 className="font-serif text-3xl mb-2">Verify your email</h1>
          <p className="text-muted-foreground mb-6">We sent a verification link to <strong>{pending}</strong>. Click it, then sign in to continue. (Check spam folder if you don't see it)</p>
          <button
            data-testid="resend-verification"
            onClick={async () => { try { await resendVerification(); toast.success("Verification email re-sent"); } catch { toast.error("Could not resend"); } }}
            className="text-terracotta underline underline-offset-4"
          >
            Resend verification email
          </button>
          <div className="mt-6">
            <button
              onClick={async () => { await signOut(); nav("/login"); }}
              className="px-5 py-2 rounded-full bg-terracotta text-primary-foreground"
            >
              Go to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src="/images/signup_bag.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900" alt="Handcrafted collection" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-terracotta/30" />
        <div className="absolute bottom-10 left-10 right-10 text-primary-foreground">
          <div className="overline mb-2 opacity-80">Lineacraft India</div>
          <h2 className="font-serif text-4xl">Begin your collection.</h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-5">
          <div>
            <div className="overline text-terracotta mb-1">Sign Up</div>
            <h1 className="font-serif text-4xl">Create account</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Sign up to save orders, addresses, and wishlist.
            </p>
          </div>

          {errorNotice && (
            <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-700 dark:text-amber-400 space-y-1">
              <div className="font-semibold flex items-center gap-1.5">
                <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                Firebase Auth Note
              </div>
              <p>{errorNotice}</p>
            </div>
          )}

          <button
            type="button"
            data-testid="signup-google-button"
            disabled={busy}
            onClick={handleGoogleSignUp}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-full border border-border bg-card hover:bg-muted font-medium text-sm transition shadow-xs disabled:opacity-50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"/>
              <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"/>
              <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z"/>
              <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"/>
            </svg>
            Sign up with Google
          </button>

          <div className="relative flex items-center justify-center my-3">
            <div className="border-t border-border w-full"></div>
            <span className="bg-background px-3 text-xs text-muted-foreground uppercase tracking-wider">or with email</span>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <label className="block">
              <span className="block text-sm mb-1.5">Full name</span>
              <input data-testid="signup-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-muted/50 border border-border px-4 py-3 rounded-md outline-none focus:border-foreground" />
            </label>
            <label className="block">
              <span className="block text-sm mb-1.5">Email</span>
              <input data-testid="signup-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-muted/50 border border-border px-4 py-3 rounded-md outline-none focus:border-foreground" />
            </label>
            <label className="block">
              <span className="block text-sm mb-1.5">Password (min 6 chars)</span>
              <input data-testid="signup-password" required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full bg-muted/50 border border-border px-4 py-3 rounded-md outline-none focus:border-foreground" />
            </label>
            <button data-testid="signup-submit" disabled={busy} className="w-full py-3 rounded-full bg-terracotta text-primary-foreground hover:opacity-90 disabled:opacity-50">{busy ? "Creating…" : "Create account"}</button>
            <div className="text-sm text-center text-muted-foreground pt-1">
              Already have an account? <Link data-testid="goto-login" to="/login" className="text-terracotta underline underline-offset-4">Sign in</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
